'use client';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, Heading, NextImage, Text } from '@/components';
import ShareIconButton from '@/components/Button/Share';
import HeartIconButton from '@/components/Button/Heart';
import { russolo } from '@/config/fonts';
import { Product } from '@/models/product';
import {
  checkItemInWishlist,
  getArticlePath,
  getKeyValue,
  pushGA4Event,
} from '@/utils/helper';
import { IMAGE_URI } from '@/config/endpoints';

import { useWishlist } from '@/context/wishlistContext';

import { usePathname, useRouter } from 'next/navigation';
import { useCleverTap } from '@/context/clevertapContext';
import { searchParams } from '@/utils/helper';

type ClassNamesTypes = {
  heading?: string;
  readTime: string;
  btn?: string;
  img?: {
    root: string;
  };
  shareBtn?: {
    btn: string;
    svg: string;
  };
  wishlistBtn?: {
    btn: string;
    svg: string;
    fillColor?: string;
  };
};

type ArticleCardPropTypes = {
  article: Product;
  classNames?: ClassNamesTypes;
  tag?: string;
  searchResult?: 'DIRECT' | 'AUTO_COMPLETE';
};

const ArticleCard: FC<ArticleCardPropTypes> = props => {
  const { article, classNames, searchResult } = props;
  const { pushEvent } = useCleverTap();
  const pathname = usePathname();
  const router = useRouter();
  const { addToWishlist, removeFromWishlist, wishlistItems } = useWishlist();
  const { name, sku, custom_attributes } = article || {};
  const customAttributes =
    custom_attributes && getKeyValue(custom_attributes, 'attribute_code');
  const attributSetName = article?.productByWizzy
    ? customAttributes?.wz_attribute_set_id?.toLowerCase()
    : article?.extension_attributes?.attribute_set_name;
  const selectedSku = attributSetName?.includes('child')
    ? `${article?.sku.split('-')[0]}?`
    : article.sku;
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams();
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );
  const link = attributSetName?.includes('child')
    ? `${getArticlePath(attributSetName)}/${selectedSku}${createQueryString(
        'sku',
        sku
      )}`
    : `${getArticlePath(attributSetName)}/${selectedSku}`;

  const isWishlisted = useMemo(
    () => checkItemInWishlist(wishlistItems, sku),
    [wishlistItems]
  );

  async function toggleWishlist(e: MouseEvent<HTMLButtonElement>, item: any) {
    e.preventDefault();
    e.stopPropagation();
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    if (!isWishlisted) {
      pushEvent({
        event: 'Search Result Wishlist',
        data: {
          'Search Keyword': searchQuery,
          'Search Type': searchResult,
          'Product SKU': sku,
          'Product Name': name,
          'Product Type': attributSetName,
        },
      });
      addToWishlist(item);
    } else {
      removeFromWishlist(item);
    }
  }

  function handleArticleClick() {
    const searchQuery = (
      document.getElementById('searchBar') as HTMLInputElement
    )?.value;
    const dataToPush: any = {
      'Page Name': window.document.title,
      'Page Url': pathname,
      SKU: sku,
      Type: attributSetName,
    };
    pushEvent({
      event: 'Article Clicked',
      data: dataToPush,
    });
    pushEvent({
      event: 'content_card_clicked',
      data: { content_id: sku },
    });
    pushEvent({
      event: 'Search Result Clicked',
      data: {
        'Search Keyword': searchQuery,
        'Search Type': searchResult,
        'Product SKU': sku,
        'Product Name': name,
        'Product Type': attributSetName,
      },
    });

    pushGA4Event('content_card_clicked', {
      category: 'Content',
      content_id: sku,
      type: attributSetName,
    });
  }

  return (
    <div
      className='col-span-1 row-span-1 w-full max-w-76.5 cursor-pointer space-y-4 rounded-md '
      onClick={() => {
        handleArticleClick();
        router.push(link);
      }}>
      <div
        className={twMerge(
          `relative aspect-square w-full rounded-md`,
          classNames?.img?.root
        )}>
        <NextImage
          fill
          src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${customAttributes?.thumbnail}`}
          alt={name}
          className='object-cover'
          sizes='40vw'
        />
        <div className='absolute right-4 top-4'>
          <div className='flex flex-col gap-3'>
            <HeartIconButton
              isWishlisted={isWishlisted}
              classNames={classNames?.wishlistBtn}
              onClick={(e: any) =>
                toggleWishlist(e, {
                  ...article,
                  sku: selectedSku,
                })
              }
            />
            <ShareIconButton
              details={{ link, title: name, type: 'story' }}
              classNames={classNames?.shareBtn}
            />
          </div>
        </div>
      </div>
      <div className='space-y-4'>
        <div className='space-y-1'>
          <Heading
            as='h4'
            className={twMerge(
              `line-clamp-3 text-base text-black md:text-[1.5rem]`,
              russolo?.className,
              classNames?.heading
            )}>
            {name}
          </Heading>
          <Text
            className={twMerge(
              `text-base font-semibold text-black/50`,
              classNames?.readTime
            )}>
            {customAttributes?.time_to_read}
          </Text>
        </div>
        <Button
          variant='link'
          label='Read Article'
          className={twMerge(
            `px-3 py-0 text-lg font-bold text-fig underline`,
            classNames?.btn
          )}
        />
      </div>
    </div>
  );
};

export default ArticleCard;
